import { Program } from '@coral-xyz/anchor'
import { createAssociatedTokenAccountInstruction } from '@solana/spl-token'
import { Keypair, PublicKey, Transaction } from '@solana/web3.js'
import { BN } from 'bn.js'
import {
  claimStakingPoolPositionInstruction,
  depositStakingPoolInstruction,
  findAssociatedTokenAddress,
  getPoolPublicKeyForMint,
  getUshMintPublicKey,
  Vault,
  withdrawStakingPoolInstruction,
} from 'hedge-web3'
import _ from 'underscore'
import TransactionStep from '../../lib/TransactionStep'
import { StakingPoolPosition } from '../accounts/StakingPoolPosition'
import VaultType from '../accounts/VaultType'
import { getHedgeMintPublicKey, HEDGE_PROGRAM_PUBLICKEY } from '../utils/HedgeConstants'
import {
  GenerateHedgeStakingPoolATAsInstructions,
  GenerateUserATAsInstructions,
} from './AssociatedTokenAccounts'
import { NATIVE_MINT as WRAPPED_SOL_MINT, createCloseAccountInstruction } from '@solana/spl-token'

export async function getCloseHedgeStakingPoolPositionTransactionStep(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  poolPosition: StakingPoolPosition
): Promise<TransactionStep> {
  console.log('getCloseHedgeStakingPoolPositionTransactionStep')
  return new TransactionStep('Close Staking Pool', async () => {
    const hedgeMintPublickey = await getHedgeMintPublicKey()

    const { blockhash } = await program.provider.connection.getRecentBlockhash()
    const transaction = new Transaction({
      feePayer: payerPublicKey,
      recentBlockhash: blockhash,
    })

    // Check if the user has a USH associated token account
    const ushMint = await getUshMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const userUshAssociatedTokenAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      ushMint
    )
    const userUshATAInfo = await program.provider.connection.getAccountInfo(
      userUshAssociatedTokenAccount
    )

    if (!userUshATAInfo) {
      console.log('Failed to find a user associated token for USH. Adding instruction.')
      transaction.add(
        createAssociatedTokenAccountInstruction(
          payerPublicKey,
          userUshAssociatedTokenAccount,
          payerPublicKey,
          ushMint
        )
      )
    }

    // Close the position
    transaction.add(
      await withdrawStakingPoolInstruction(
        program,
        payerPublicKey,
        poolPosition.publicKey,
        hedgeMintPublickey
      )
    )

    return transaction
  })
}

export async function getClaimHedgeStakingPoolPositionTransactionStep(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  poolPosition: StakingPoolPosition
): Promise<TransactionStep> {
  console.log('getClaimHedgeStakingPoolPositionTransactionStep')

  return new TransactionStep('Claim Staking Pool', async () => {
    const hedgeMintPublickey = await getHedgeMintPublicKey()

    const { blockhash } = await program.provider.connection.getRecentBlockhash()
    const transaction = new Transaction({
      feePayer: payerPublicKey,
      recentBlockhash: blockhash,
    })

    // Check if the user has a USH associated token account
    const ushMint = await getUshMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const userUshAssociatedTokenAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      ushMint
    )
    const userUshATAInfo = await program.provider.connection.getAccountInfo(
      userUshAssociatedTokenAccount
    )

    if (!userUshATAInfo) {
      console.log('Failed to find a user associated token for USH. Adding instruction.')
      transaction.add(
        createAssociatedTokenAccountInstruction(
          payerPublicKey,
          userUshAssociatedTokenAccount,
          payerPublicKey,
          ushMint
        )
      )
    }

    // Create any needed associated token accounts
    // Get all the vault types
    const vaultTypes = (await program.account.vaultType.all()).map((vaultType) => {
      return new VaultType(vaultType.account, vaultType.publicKey)
    })

    // Create any user associated token accounts if needed
    const userAtcInstructions = await GenerateUserATAsInstructions(
      program,
      vaultTypes,
      payerPublicKey
    )
    if (userAtcInstructions.length > 0) {
      transaction.add(...userAtcInstructions)
    }

    // Create any pool associated token accounts if needed
    const poolAtcInstructions = await GenerateHedgeStakingPoolATAsInstructions(
      program,
      vaultTypes,
      payerPublicKey
    )
    if (poolAtcInstructions.length > 0) {
      transaction.add(...poolAtcInstructions)
    }

    // Add a claim instruction or each vault type
    let closeWrappedSolAfter = false
    for (let index = 0; index < vaultTypes.length; index++) {
      console.log('Adding claim instruction for vault type slug', vaultTypes[index].collateral)
      const payerAssociatedTokenAccount = await findAssociatedTokenAddress(
        HEDGE_PROGRAM_PUBLICKEY,
        payerPublicKey,
        vaultTypes[index].collateralMint
      )

      const hedgeMint = await getHedgeMintPublicKey()
      const [feePool] = await getPoolPublicKeyForMint(HEDGE_PROGRAM_PUBLICKEY, hedgeMint)
      const feePoolAssociatedTokenAccount = await findAssociatedTokenAddress(
        HEDGE_PROGRAM_PUBLICKEY,
        feePool,
        vaultTypes[index].collateralMint
      )

      if (poolPosition.getRewardsAtIndex(vaultTypes[index].collateralIndex).gt(0)) {
        transaction.add(
          await claimStakingPoolPositionInstruction(
            program,
            feePool,
            feePoolAssociatedTokenAccount,
            hedgeMint,
            vaultTypes[index].publicKey,
            vaultTypes[index].collateralMint,
            poolPosition.publicKey,
            payerPublicKey,
            payerAssociatedTokenAccount
          )
        )
      }

      // If the token was wrapped SOL, we'll unwrap it for the user.
      if (vaultTypes[index].collateralMint.toString() === WRAPPED_SOL_MINT.toString()) {
        closeWrappedSolAfter = true
      }
    }

    transaction.add(
      await program.methods
        .closeClaimedStakingPoolPosition()
        .accounts({
          poolPosition: poolPosition.publicKey,
          payer: payerPublicKey,
        })
        .instruction()
    )

    if (closeWrappedSolAfter) {
      // If the token was wrapped SOL, we'll unwrap it for the user.
      const payerWrappedSolAssociatedTokenAccount = await findAssociatedTokenAddress(
        HEDGE_PROGRAM_PUBLICKEY,
        payerPublicKey,
        WRAPPED_SOL_MINT
      )
      transaction.add(
        createCloseAccountInstruction(
          payerWrappedSolAssociatedTokenAccount,
          payerPublicKey,
          payerPublicKey
        )
      )
    }

    return transaction
  })
}

// export async function getWithdrawHedgeStakingPoolTransactionSteps(
//   program: Program<Vault>,
//   payerPublicKey: PublicKey,
//   poolPosition: StakingPoolPosition
// ): Promise<TransactionStep[]> {
//   console.log('getWithdrawHedgeStakingPoolTransactionSteps')

//   const steps: TransactionStep[] = []
//   const hedgeMintPublickey = await getHedgeMintPublicKey()

//   const { blockhash } = await program.provider.connection.getRecentBlockhash()
//   const transaction = new Transaction({
//     feePayer: payerPublicKey,
//     recentBlockhash: blockhash,
//   })

//   // Check if the user has a USH associated token account
//   const ushMint = await getUshMintPublicKey()
//   const userUshAssociatedTokenAccount = await findAssociatedTokenAddress(payerPublicKey, ushMint)
//   const userUshATAInfo = await program.provider.connection.getAccountInfo(
//     userUshAssociatedTokenAccount
//   )

//   if (!userUshATAInfo) {
//     console.log('Failed to find a user associated token for USH. Adding instruction.')
//     transaction.add(
//       createAssociatedTokenAccountInstruction(
//         payerPublicKey,
//         userUshAssociatedTokenAccount,
//         payerPublicKey,
//         ushMint
//       )
//     )
//   }

//   // Close the position
//   transaction.add(
//     await withdrawStakingPoolInstruction(
//       program,
//       payerPublicKey,
//       poolPosition.publicKey,
//       hedgeMintPublickey
//     )
//   )

//   // Create any needed associated token accounts
//   // Get all the vault types
//   const vaultTypes = (await program.account.vaultType.all()).map((vaultType) => {
//     return new VaultType(vaultType.account, vaultType.publicKey)
//   })

//   // Create any user associated token accounts if needed
//   const userAtcInstructions = await GenerateUserATAsInstructions(
//     program,
//     vaultTypes,
//     payerPublicKey
//   )
//   if (userAtcInstructions.length > 0) {
//     transaction.add(...userAtcInstructions)
//   }

//   // Create any pool associated token accounts if needed
//   const poolAtcInstructions = await GenerateHedgeStakingPoolATAsInstructions(
//     program,
//     vaultTypes,
//     payerPublicKey
//   )
//   if (poolAtcInstructions.length > 0) {
//     transaction.add(...poolAtcInstructions)
//   }

//   // Add a claim instruction or each vault type
//   let closeWrappedSolAfter = false
//   for (let index = 0; index < vaultTypes.length; index++) {
//     console.log('Adding claim instruction for vault type slug', vaultTypes[index].collateral)
//     const payerAssociatedTokenAccount = await findAssociatedTokenAddress(
//       payerPublicKey,
//       vaultTypes[index].collateralMint
//     )

//     const hedgeMint = await getHedgeMintPublicKey()
//     const [feePool] = await getPoolPublicKeyForMint(hedgeMint)
//     const feePoolAssociatedTokenAccount = await findAssociatedTokenAddress(
//       feePool,
//       vaultTypes[index].collateralMint
//     )

//     // Add the claim position instruction for this vault type
//     // HACK - Coudron needs to fix this. Break the claim into multiple transactions?
//     if (
//       vaultTypes[index].collateralMint.toString() != '993dVFL2uXWYeoXuEBFXR4BijeXdTv4s6BzsCjJZuwqk'
//     ) {
//       transaction.add(
//         await claimStakingPoolPositionInstruction(
//           program,
//           feePool,
//           feePoolAssociatedTokenAccount,
//           hedgeMint,
//           vaultTypes[index].publicKey,
//           vaultTypes[index].collateralMint,
//           poolPosition.publicKey,
//           payerPublicKey,
//           payerAssociatedTokenAccount
//         )
//       )
//     }

//     // If the token was wrapped SOL, we'll unwrap it for the user.
//     if (vaultTypes[index].collateralMint.toString() === WRAPPED_SOL_MINT.toString()) {
//       closeWrappedSolAfter = true
//     }
//   }

//   transaction.add(
//     await program.methods
//       .closeClaimedStakingPoolPosition()
//       .accounts({
//         poolPosition: poolPosition.publicKey,
//         payer: payerPublicKey,
//       })
//       .instruction()
//   )

//   if (closeWrappedSolAfter) {
//     // If the token was wrapped SOL, we'll unwrap it for the user.
//     const payerWrappedSolAssociatedTokenAccount = await findAssociatedTokenAddress(
//       payerPublicKey,
//       WRAPPED_SOL_MINT
//     )
//     transaction.add(
//       TokenInstructions.closeAccount({
//         source: payerWrappedSolAssociatedTokenAccount,
//         destination: payerPublicKey,
//         owner: payerPublicKey,
//       })
//     )
//   }

//   return transaction
// }

export async function getDepositHedgeStakingPoolTransactionStep(
  program: Program<Vault>,
  userWalletPublicKey: PublicKey,
  depositAmountInLamports: number
): Promise<TransactionStep> {
  return new TransactionStep('Deposit Staking Pool', async () => {
    let transaction: Transaction

    // // If there's an existing position, withdraw it and re-deposit.
    // if (existingPosition) {
    //   console.log('Found existing position', existingPosition)
    //   depositAmount = existingPosition.deposited + amountInLamports
    //   transaction = await getWithdrawHedgeStakingPoolTransaction(
    //     program,
    //     userWalletPublicKey,
    //     existingPosition
    //   )
    //   // Else, just create a new transaction
    // } else {
    const { blockhash } = await program.provider.connection.getRecentBlockhash()
    transaction = new Transaction({
      feePayer: userWalletPublicKey,
      recentBlockhash: blockhash,
    })
    // }

    const hedgeMintPublickey = await getHedgeMintPublicKey()
    const newPoolPosition = Keypair.generate()

    // Create deposit instruction
    transaction.add(
      await depositStakingPoolInstruction(
        program,
        userWalletPublicKey,
        newPoolPosition.publicKey,
        hedgeMintPublickey,
        new BN(depositAmountInLamports)
      )
    )
    transaction.partialSign(newPoolPosition)

    return transaction
  })
}
